import { z } from 'zod';
/**
 * Vocabulario compartido entre la API y el front.
 *
 * Los literales tienen que coincidir con los enums de Prisma. Si cambiás uno acá,
 * cambiá el schema y generá la migración — no hay validación cruzada automática.
 */
export const ROLES = ['SUPERADMIN', 'PHARMA_ADMIN', 'CASE_MANAGER', 'PATIENT', 'CAREGIVER'];
export const roleSchema = z.enum(ROLES);
export const ROLE_LABELS = {
    SUPERADMIN: 'Administrador Dx Tech',
    PHARMA_ADMIN: 'Administrador de la institución',
    CASE_MANAGER: 'Equipo de seguimiento',
    PATIENT: 'Paciente',
    CAREGIVER: 'Cuidador',
};
/** Roles que operan la consola del tenant. */
export const CONSOLE_ROLES = ['PHARMA_ADMIN', 'CASE_MANAGER'];
/** Roles que operan la app del paciente. */
export const APP_ROLES = ['PATIENT', 'CAREGIVER'];
export const PATIENT_STATUSES = ['INVITED', 'ACTIVE', 'PAUSED', 'COMPLETED', 'WITHDRAWN'];
export const patientStatusSchema = z.enum(PATIENT_STATUSES);
export const PATIENT_STATUS_LABELS = {
    INVITED: 'Invitado',
    ACTIVE: 'Activo',
    PAUSED: 'Pausado',
    COMPLETED: 'Programa finalizado',
    WITHDRAWN: 'Retirado',
};
export const ALERT_STATUSES = ['OPEN', 'ACKNOWLEDGED', 'RESOLVED', 'DISMISSED'];
export const alertStatusSchema = z.enum(ALERT_STATUSES);
export const ALERT_STATUS_LABELS = {
    OPEN: 'Abierta',
    ACKNOWLEDGED: 'Tomada',
    RESOLVED: 'Resuelta',
    DISMISSED: 'Descartada',
};
export const CONTENT_TYPES = ['ARTICLE', 'VIDEO', 'AUDIO', 'PDF', 'LINK'];
export const contentTypeSchema = z.enum(CONTENT_TYPES);
export const CONTENT_TYPE_LABELS = {
    ARTICLE: 'Artículo',
    VIDEO: 'Video',
    AUDIO: 'Audio',
    PDF: 'PDF',
    LINK: 'Enlace',
};
/**
 * Desenlace de un evento adverso, según la terminología de farmacovigilancia.
 */
export const AE_OUTCOMES = [
    'RECOVERED',
    'RECOVERING',
    'NOT_RECOVERED',
    'RECOVERED_WITH_SEQUELAE',
    'FATAL',
    'UNKNOWN',
];
export const aeOutcomeSchema = z.enum(AE_OUTCOMES);
export const AE_OUTCOME_LABELS = {
    RECOVERED: 'Recuperado',
    RECOVERING: 'En recuperación',
    NOT_RECOVERED: 'No recuperado',
    RECOVERED_WITH_SEQUELAE: 'Recuperado con secuelas',
    FATAL: 'Desenlace fatal',
    UNKNOWN: 'Desconocido',
};
export const AE_STATUSES = ['REPORTED', 'SENT_TO_PV', 'ACKNOWLEDGED', 'CLOSED'];
export const aeStatusSchema = z.enum(AE_STATUSES);
export const AE_STATUS_LABELS = {
    REPORTED: 'Reportado',
    SENT_TO_PV: 'Derivado a farmacovigilancia',
    ACKNOWLEDGED: 'Acusado recibo',
    CLOSED: 'Cerrado',
};
/**
 * Un evento adverso serio, en el sentido regulatorio: cumple al menos uno de los
 * criterios de seriedad de ICH E2A. La distinción importa porque cambia el plazo
 * de notificación a la autoridad sanitaria.
 */
export const SERIOUSNESS_CRITERIA = [
    'DEATH',
    'LIFE_THREATENING',
    'HOSPITALIZATION',
    'DISABILITY',
    'CONGENITAL_ANOMALY',
    'MEDICALLY_SIGNIFICANT',
];
export const seriousnessCriterionSchema = z.enum(SERIOUSNESS_CRITERIA);
export const SERIOUSNESS_LABELS = {
    DEATH: 'Provocó la muerte',
    LIFE_THREATENING: 'Puso la vida en riesgo',
    HOSPITALIZATION: 'Requirió internación o la prolongó',
    DISABILITY: 'Provocó discapacidad o incapacidad persistente',
    CONGENITAL_ANOMALY: 'Anomalía congénita',
    MEDICALLY_SIGNIFICANT: 'Otro evento médicamente significativo',
};
export const TREATMENT_EVENT_TYPES = ['INFUSION', 'APPOINTMENT', 'LAB', 'IMAGING', 'OTHER'];
export const treatmentEventTypeSchema = z.enum(TREATMENT_EVENT_TYPES);
export const TREATMENT_EVENT_LABELS = {
    INFUSION: 'Infusión',
    APPOINTMENT: 'Consulta',
    LAB: 'Institución',
    IMAGING: 'Estudio por imágenes',
    OTHER: 'Otro',
};
/** Zonas del mapa corporal del diario de dolor. */
export const BODY_REGIONS = [
    { value: 'head', label: 'Cabeza' },
    { value: 'neck', label: 'Cuello' },
    { value: 'chest', label: 'Pecho' },
    { value: 'abdomen', label: 'Abdomen' },
    { value: 'back_upper', label: 'Espalda alta' },
    { value: 'back_lower', label: 'Espalda baja' },
    { value: 'pelvis', label: 'Pelvis' },
    { value: 'arm_left', label: 'Brazo izquierdo' },
    { value: 'arm_right', label: 'Brazo derecho' },
    { value: 'hand_left', label: 'Mano izquierda' },
    { value: 'hand_right', label: 'Mano derecha' },
    { value: 'leg_left', label: 'Pierna izquierda' },
    { value: 'leg_right', label: 'Pierna derecha' },
    { value: 'foot_left', label: 'Pie izquierdo' },
    { value: 'foot_right', label: 'Pie derecho' },
];
/** Índice de masa corporal. Devuelve null si falta un dato o son absurdos. */
export function computeBmi(weightKg, heightCm) {
    if (!weightKg || !heightCm)
        return null;
    if (heightCm < 50 || heightCm > 260)
        return null;
    const m = heightCm / 100;
    return Math.round((weightKg / (m * m)) * 10) / 10;
}
/** Clasificación de IMC de la OMS. */
export function bmiCategory(bmi) {
    if (bmi === null)
        return null;
    if (bmi < 18.5)
        return 'Bajo peso';
    if (bmi < 25)
        return 'Peso normal';
    if (bmi < 30)
        return 'Sobrepeso';
    return 'Obesidad';
}
//# sourceMappingURL=domain.js.map